import { useState, useContext } from "react";
import { FaEdit, FaIdBadge, FaPlusCircle, FaTrashAlt, FaUser, FaUserTie, FaBriefcase, FaCalendarAlt, FaUserFriends, FaClipboardList } from "react-icons/fa";
import Button from "components/ui/controls/button/button";
import CardBox from "components/ui/controls/card-box/card-box";
import { CardBoxHeader } from "components/ui/controls/card-box/card-box";
import Column from "components/ui/layout/column/column";
import Row from "components/ui/layout/row/row";
import Modal from "components/ui/modal/modal";
import { AppContext } from "components/app/RemExprertProvider";
import SituationProPageModal from "./modules/situation professionnelle pager";
import { applyFormat } from "components/ui/ui-helpers";
import Dialogs from "components/ui/dialogs/dialogs";
import { createStyledButton } from "components/ui/controls/button/button-styles";
import "./SituationProfessionnelle.css";
import { CustomTooltip } from "components/ui/controls/custom-tooltip/custom-tooltip";

// Composant de tooltip personnalisé moderne


// Fonction utilitaire pour le mapping des situations professionnelles
const handleSitPro = (Statut, Caisse) => {
  if (Statut === "TNS Article 62" || Statut === "TNS individuel") {
    const corrCaisseSitPro = {
      RsiArtisan: "Artisan",
      RsiCommercant: "Commerçant",
      Carpv: "Vétérinaire",
      Cavec: "Expert comptable",
      Cipav: "Profession libérale",
      Crn: "Notaire",
      Cnbf: "Avocat",
      Cavamac: "Agent général d'assurance",
      Cavom: "Officier ministériel",
      Msa: "Exploitant agricole",
      Carmf: "Médecin",
      Carpimko: "Auxiliaire médical",
      Carcdsf: "Chirurgien-dentiste",
      Carcdsf2: "Sage-femme",
      Cavp: "Pharmacien",
    };
    return corrCaisseSitPro[Caisse];
  }
  return Statut;
};

// Fonction pour obtenir le nom complet de la caisse
const getCaisseFullName = (Caisse) => {
  const caisseNames = {
    RsiArtisan: "SSI Artisan",
    RsiCommercant: "SSI Commerçant",
    Carpv: "CARPV (Vétérinaires)",
    Cavec: "CAVEC (Experts-comptables)",
    Cipav: "CIPAV (Professions libérales)",
    Crn: "CRN (Notaires)",
    Cnbf: "CNBF (Avocats)",
    Cavamac: "CAVAMAC (Agents généraux)",
    Cavom: "CAVOM (Officiers ministériels)",
    Msa: "MSA (Exploitants agricoles)",
    Carmf: "CARMF (Médecins)",
    Carpimko: "CARPIMKO (Auxiliaires médicaux)",
    Carcdsf: "CARCDSF (Chirurgiens-dentistes)",
    Carcdsf2: "CARCDSF (Sages-femmes)",
    Cavp: "CAVP (Pharmaciens)",
    Regime: "Régime général",
  };
  return caisseNames[Caisse] || Caisse;
};

// Fonction pour formater l'affichage complet du statut
const getFullStatusDisplay = (Statut, Caisse) => {
  const profession = handleSitPro(Statut, Caisse);
  const caisse = Statut !== "TNS Article 62" && Statut !== "TNS individuel" ? "Régime général" : getCaisseFullName(Caisse);

  // Si c'est un TNS, afficher: "TNS Article 62 - Médecin • CARMF (Médecins)"
  if (Statut === "TNS Article 62" || Statut === "TNS individuel") {
    return `${Statut} - ${profession} • ${caisse}`;
  }

  // Pour les autres statuts, afficher: "Assimilé salarié • Régime général"
  return `${Statut} • ${caisse}`;
};

// Composant moderne pour l'état vide
const EmptyStateMessage = ({ type, personName }) => {
  const isConjoint = type === "conjoint";

  return (
    <div className="empty-state-container">
      <div className="empty-state-content">
        <div className="empty-state-illustration">
          <div className="empty-state-icon-bg">{isConjoint ? <FaUserFriends className="empty-state-icon" /> : <FaClipboardList className="empty-state-icon" />}</div>
          <div className="empty-state-decoration">
            <div className="decoration-dot decoration-dot-1"></div>
            <div className="decoration-dot decoration-dot-2"></div>
            <div className="decoration-dot decoration-dot-3"></div>
          </div>
        </div>

        <div className="empty-state-text">
          <h3 className="empty-state-title">{isConjoint ? "Aucune situation professionnelle" : "Commencez par créer une situation"}</h3>
          <p className="empty-state-subtitle">
            {isConjoint ? `Aucune situation professionnelle n'a été renseignée pour ${personName}` : `Renseignez la situation professionnelle de ${personName} pour continuer l'audit`}
          </p>
        </div>

        {!isConjoint && (
          <div className="empty-state-hint">
            <div className="hint-icon">
              <FaBriefcase />
            </div>
            <span>Cliquez sur le bouton ci-dessus pour commencer</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Composant SituationProCard moderne
const SituationProCard = ({ periode, onEdit, onDelete }) => {
  return (
    <div className="situation-pro-card">
      <div className="situation-pro-card-main">
        <div className="situation-pro-card-info">
          <div className="situation-pro-card-icon">
            <FaBriefcase style={{ fontSize: "16px", color: "#3b82f6" }} />
          </div>

          <div className="situation-pro-card-details">
            <h4 className="situation-pro-card-title">{periode.IdentiteEntreprise || "Entreprise non renseignée"}</h4>
            <p className="situation-pro-card-subtitle">{getFullStatusDisplay(periode.Statut, periode.Caisse)}</p>
            <div className="situation-pro-card-dates">
              <FaCalendarAlt style={{ fontSize: "10px" }} />
              <span>
                Du {applyFormat(periode.DebutPeriodeCarriereActuelle, "date", "")} au {applyFormat(periode.FinPeriodeCarriereActuelle, "date", "")}
              </span>
            </div>
          </div>
        </div>
        <div className="situation-pro-card-actions">
          <Button onClick={onEdit} icon {...createStyledButton("card-action", "cardEdit")}>
            <FaEdit id="editSituationPro" style={{ color: "#22c55e", fontSize: "12px" }} />
          </Button>
          <Button onClick={onDelete} icon {...createStyledButton("card-action", "delete")}>
            <FaTrashAlt style={{ color: "#ef4444", fontSize: "12px" }} />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Composant pour les cartes de situation professionnelle du conjoint
const SituationProConjCard = ({ periode, isFromPeriodeActuelle = false, onEdit, onDelete }) => {
  const statut = isFromPeriodeActuelle ? periode.StatutConjoint : periode.Statut;
  const finPeriode = isFromPeriodeActuelle ? periode.FinPeriodeCarriereActuelleConjoint : periode.FinPeriodeCarriereActuelle;

  return (
    <div className="situation-pro-card">
      <div className="situation-pro-card-main">
        <div className="situation-pro-card-info">
          <div className="situation-pro-card-icon">
            <FaBriefcase style={{ fontSize: "16px", color: "#10b981" }} />
          </div>

          <div className="situation-pro-card-details">
            <h4 className="situation-pro-card-title">{periode.IdentiteEntreprise || "Entreprise non renseignée"}</h4>
            <p className="situation-pro-card-subtitle">{getFullStatusDisplay(statut, periode.Caisse)}</p>
            <div className="situation-pro-card-dates">
              <FaCalendarAlt style={{ fontSize: "10px" }} />
              <span>
                Du {applyFormat(periode.DebutPeriodeCarriereActuelle, "date", "")} au {applyFormat(finPeriode, "date", "")}
              </span>
            </div>
          </div>
        </div>
        {!isFromPeriodeActuelle && (
          <div className="situation-pro-card-actions">
            <Button onClick={onEdit} icon {...createStyledButton("card-action", "cardEdit")}>
              <FaEdit style={{ color: "#22c55e", fontSize: "12px" }} />
            </Button>
            <Button onClick={onDelete} icon {...createStyledButton("card-action", "delete")}>
              <FaTrashAlt style={{ color: "#ef4444", fontSize: "12px" }} />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const SituationProPage = (props) => {
  const context = useContext(AppContext);
  const [clientConjoint, setClientConjoint] = useState("");
  const [modalOpen, setmodalOpen] = useState(false);
  return (
    <>
      <Column fill>
        <Row>
          <Column fill>
            <CardBox>
              <Column fill>
                <CardBoxHeader icon={<FaUser />}>Situation professionnelle pour {context.state.Prenom}</CardBoxHeader>
                <Row center>
                  <Column style={{ paddingLeft: 0 }}>
                    <CustomTooltip
                      content={
                        context.state.PeriodeActuelle.length > 0
                          ? "🚫 Limitation atteinte\nUne seule situation professionnelle peut être créée par personne"
                          : "✨ Nouveau\nCréer la situation professionnelle du titulaire"
                      }
                      type={context.state.PeriodeActuelle.length > 0 ? "warning" : "success"}
                      disabled={context.state.PeriodeActuelle.length > 0}
                    >
                      <Button
                        {...createStyledButton("primary", "primary", {}, context.state.PeriodeActuelle.length > 0)}
                        onClick={() => {
                          if (context.state.PeriodeActuelle.length === 0) {
                            setClientConjoint("");
                            setmodalOpen(true);
                          }
                        }}
                      >
                        <FaPlusCircle style={{ marginRight: "8px" }} />
                        Créer une nouvelle situation professionnelle
                      </Button>
                    </CustomTooltip>
                  </Column>
                </Row>

                {context.state.PeriodeActuelle.length > 0 ? (
                  <Row tpad>
                    <Column fill>
                      <div className="situation-pro-scroll">
                        {context.state.PeriodeActuelle.map((Infos, Index) => (
                          <SituationProCard
                            key={Index}
                            periode={Infos}
                            onEdit={() => {
                              context.handleModifyDeletePeriodeActuelle(Infos.IdPeriodeActuelle, "Modifier", "", "");
                              setClientConjoint("");
                              setmodalOpen(true);
                            }}
                            onDelete={() => {
                              Dialogs.confirm("Vous êtes sur le point de supprimer la période.\nVoulez-vous continuer ?", "Suppression de la période", () => {
                                context.handleModifyDeletePeriodeActuelle(Infos.IdPeriodeActuelle, "Supprimer", "", "");
                              });
                            }}
                          />
                        ))}
                      </div>
                    </Column>
                  </Row>
                ) : (
                  <Row>
                    <Column>
                      <EmptyStateMessage type="titulaire" personName={context.state.Prenom} />
                    </Column>
                  </Row>
                )}
              </Column>
            </CardBox>
          </Column>
        </Row>
        {context.state.InclureConjoint && (
          <Row>
            <Column fill>
              <CardBox tpad>
                <Column fill>
                  <CardBoxHeader icon={<FaUserTie />}>Situation professionnelle pour {context.state.PrenomConjoint}</CardBoxHeader>
                  <Row center>
                    <Column style={{ paddingLeft: 0 }}>
                      <CustomTooltip
                        content={
                          context.state.PeriodeActuelle.length === 0
                            ? "⚠️ Prérequis manquant\nVeuillez d'abord créer une situation professionnelle pour le titulaire"
                            : context.state.PeriodeActuelleConjoint.length > 0
                            ? "🚫 Limitation atteinte\nUne seule situation professionnelle peut être créée pour le conjoint"
                            : context.state.PeriodeActuelle[0]?.StatutConjoint !== undefined && context.state.PeriodeActuelle[0]?.StatutConjoint !== "Aucun"
                            ? "ℹ️ Déjà configuré\nLe statut du conjoint est déjà renseigné dans la situation du titulaire"
                            : "👥 Nouveau conjoint\nCréer la situation professionnelle du conjoint"
                        }
                        type={
                          context.state.PeriodeActuelle.length === 0
                            ? "warning"
                            : context.state.PeriodeActuelleConjoint.length > 0
                            ? "warning"
                            : context.state.PeriodeActuelle[0]?.StatutConjoint !== undefined && context.state.PeriodeActuelle[0]?.StatutConjoint !== "Aucun"
                            ? "info"
                            : "success"
                        }
                        disabled={
                          context.state.PeriodeActuelle.length === 0 ||
                          context.state.PeriodeActuelleConjoint.length > 0 ||
                          (context.state.PeriodeActuelle[0]?.StatutConjoint !== undefined && context.state.PeriodeActuelle[0]?.StatutConjoint !== "Aucun")
                        }
                      >
                        <Button
                          {...createStyledButton(
                            "primary",
                            "primary",
                            {},
                            context.state.PeriodeActuelle.length === 0 ||
                              context.state.PeriodeActuelleConjoint.length > 0 ||
                              (context.state.PeriodeActuelle[0]?.StatutConjoint !== undefined && context.state.PeriodeActuelle[0]?.StatutConjoint !== "Aucun")
                              ? true
                              : false
                          )}
                          onClick={() => {
                            const isDisabled =
                              context.state.PeriodeActuelle.length === 0 ||
                              context.state.PeriodeActuelleConjoint.length > 0 ||
                              (context.state.PeriodeActuelle[0]?.StatutConjoint !== undefined && context.state.PeriodeActuelle[0]?.StatutConjoint !== "Aucun");

                            if (!isDisabled) {
                              setClientConjoint("Conjoint");
                              setmodalOpen(true);
                            }
                          }}
                        >
                          <FaPlusCircle style={{ marginRight: "8px" }} />
                          Créer une situation professionnelle conjoint
                        </Button>
                      </CustomTooltip>
                    </Column>
                  </Row>
                  {context.state.PeriodeActuelleConjoint.length > 0 ||
                  (context.state.PeriodeActuelle[0]?.StatutConjoint !== undefined && context.state.PeriodeActuelle[0]?.StatutConjoint !== "Aucun") ? (
                    <Row tpad>
                      <Column fill>
                        <div className="situation-pro-scroll">
                          {/* Périodes du conjoint depuis PeriodeActuelle */}
                          {context.state.PeriodeActuelle[0]?.StatutConjoint !== undefined && context.state.PeriodeActuelle[0]?.StatutConjoint !== "Aucun" && (
                            <>
                              {context.state.PeriodeActuelle.map((Infos, Index) => (
                                <SituationProConjCard key={"Conj" + Index} periode={Infos} isFromPeriodeActuelle={true} />
                              ))}
                            </>
                          )}

                          {/* Périodes du conjoint depuis PeriodeActuelleConjoint */}
                          {context.state.PeriodeActuelleConjoint.map((Infos, Index) => (
                            <SituationProConjCard
                              key={"Conj2" + Index}
                              periode={Infos}
                              isFromPeriodeActuelle={false}
                              onEdit={() => {
                                context.handleModifyDeletePeriodeActuelle(Infos.IdPeriodeActuelle, "Modifier", "", "Conjoint");
                                setClientConjoint("Conjoint");
                                setmodalOpen(true);
                              }}
                              onDelete={() => {
                                Dialogs.confirm("Vous êtes sur le point de supprimer la période.\nVoulez-vous continuer ?", "Suppression de la période", () => {
                                  context.handleModifyDeletePeriodeActuelle(Infos.IdPeriodeActuelle, "Supprimer", "", "Conjoint");
                                });
                              }}
                            />
                          ))}
                        </div>
                      </Column>
                    </Row>
                  ) : (
                    <Row>
                      <Column>
                        <EmptyStateMessage type="conjoint" personName={context.state.PrenomConjoint} />
                      </Column>
                    </Row>
                  )}
                </Column>
              </CardBox>
            </Column>
          </Row>
        )}
      </Column>
      <Modal
        width="1400px"
        height="82%"
        title="Situation actuelle et projetée"
        icon={<FaIdBadge />}
        visible={modalOpen}
        onClose={() => {
          setmodalOpen(false);
        }}
      >
        <SituationProPageModal
          Outil=""
          ClientConjoint={clientConjoint}
          visible={modalOpen}
          onClose={() => {
            setmodalOpen(false);
          }}
        />
      </Modal>
    </>
  );
};

export default SituationProPage;
